import { motion } from "framer-motion";
import { Phone, ArrowUpRight } from "lucide-react";
import { BRAND } from "../lib/api";

const TEAM_IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-quote-70/artifacts/wc72139e_IMG_4445.webp";

export default function Team() {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative py-24 md:py-40 border-t border-brand-border overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* Portrait — clipped frame */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, ease: [0.6, 0.05, 0.15, 1] }}
          className="lg:col-span-6 relative"
        >
          <div className="relative aspect-[3/4] w-full overflow-hidden bg-brand-surface border border-brand-border">
            <img
              src={TEAM_IMG}
              alt="RRR Paint & Construction crew painting an interior job site"
              loading="lazy"
              decoding="async"
              style={{ transform: "rotate(90deg) scale(1.55)" }}
              className="absolute inset-0 h-full w-full object-cover grayscale contrast-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-bg/60 via-transparent to-transparent" />

            {/* Badges */}
            <div className="absolute top-5 left-5 bg-brand-blue text-white text-[10px] tracking-[0.3em] uppercase px-3 py-1 font-semibold">
              On site
            </div>
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/70 mb-1">
                  Wind Creek · Wetumpka
                </div>
                <div className="font-display uppercase text-2xl md:text-3xl leading-none">
                  RRR Blues. Brush In Hand.
                </div>
              </div>
            </div>
          </div>

          {/* accent number outside frame */}
          <div className="hidden md:block absolute -top-4 -right-4 z-10">
            <div className="font-display text-brand-blue text-[8vw] leading-none opacity-40 select-none pointer-events-none">
              04
            </div>
          </div>
        </motion.div>

        {/* Copy */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, ease: [0.6, 0.05, 0.15, 1] }}
          >
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-muted mb-6 flex items-center gap-3">
              <span className="h-px w-8 bg-brand-blue" />
              04 — Our Team
            </div>

            <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight mb-8">
              Hands On. <br />
              <span className="text-brand-blue">Every Job.</span>
            </h2>

            <p className="text-brand-muted text-base md:text-lg leading-relaxed max-w-lg mb-6">
              Our crew shows up in RRR blues, on time, and treats every job —
              big or small, residential or commercial — like it&apos;s their own
              home. Prep. Paint. Walk-through. No shortcuts.
            </p>
            <p className="text-brand-muted text-base md:text-lg leading-relaxed max-w-lg mb-10">
              That&apos;s how we back the promise on every wall:{" "}
              <span className="text-white font-semibold">
                Great Quality. Great Experience.
              </span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={BRAND.phoneHref}
                data-testid="team-call-btn"
                className="group inline-flex items-center gap-3 bg-brand-blue hover:bg-brand-blueHover text-white px-6 py-4 font-display tracking-wider text-lg md:text-xl uppercase transition-colors"
              >
                <Phone className="h-5 w-5" />
                {BRAND.phone}
              </a>
              <button
                onClick={() =>
                  document
                    .getElementById("lead-form")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                data-testid="team-quote-btn"
                className="group inline-flex items-center gap-3 border border-white/30 hover:border-white text-white px-6 py-4 font-display tracking-wider text-lg md:text-xl uppercase transition-colors"
              >
                Get Your Quote
                <ArrowUpRight className="h-5 w-5 group-hover:rotate-45 transition-transform" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
